import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  GraduationCap, Plus, Pencil, Trash2, X, BookOpen, Play,
  ChevronDown, ChevronRight, Video, FileText, Save, ArrowUp, ArrowDown,
  Upload, Image, ClipboardList, CheckCircle, XCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcademyAdmin = () => {

  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCursoModal, setShowCursoModal] = useState(false);
  const [showModuloModal, setShowModuloModal] = useState(false);
  const [showAulaModal, setShowAulaModal] = useState(false);
  const [showQuestionarioModal, setShowQuestionarioModal] = useState(false);
  const [editingCurso, setEditingCurso] = useState(null);
  const [editingModulo, setEditingModulo] = useState(null);
  const [editingAula, setEditingAula] = useState(null);
  const [editingQuestionario, setEditingQuestionario] = useState(null);
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [selectedModulo, setSelectedModulo] = useState(null);
  const [expandedCursos, setExpandedCursos] = useState({});
  const [expandedModulos, setExpandedModulos] = useState({});
  
  const [cursoForm, setCursoForm] = useState({
    titulo: "",
    descricao: "",
    imagem_capa: "",
    perfis_permitidos: ["corretor", "cadastrador", "admin", "proprietario"],
    ativo: true
  });
  
  const [uploadingCapa, setUploadingCapa] = useState(false);
  const capaInputRef = useRef(null);
  
  const [moduloForm, setModuloForm] = useState({
    titulo: "",
    descricao: "",
    ordem: 0
  });
  
  const [aulaForm, setAulaForm] = useState({
    titulo: "",
    descricao: "",
    tipo: "video",
    conteudo_url: "",
    conteudo_texto: "",
    duracao_minutos: null
  });
  
  const [questionarioForm, setQuestionarioForm] = useState({
    titulo: "",
    descricao: "",
    nota_minima: 70,
    tentativas_permitidas: 3,
    questoes: []
  });
  
  const token = localStorage.getItem('tayane_token');
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const isAdminOrOwner = currentUser?.role === "admin" || currentUser?.role === "proprietario" || currentUser?.role === "dev";

  useEffect(() => {
    if (!isAdminOrOwner) {
      toast.error("Acesso restrito");
      navigate("/academy");
      return;
    }
    fetchCursos();
  }, []);

  const fetchCursos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/academy/cursos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Carregar módulos, aulas e questionários de cada curso
      const cursosCompletos = await Promise.all(
        (Array.isArray(response.data) ? response.data : (response.data?.cursos || response.data?.items || [])).map(async (curso) => {
          const modulosRes = await axios.get(`${BACKEND_URL}/api/academy/cursos/${curso.id}/modulos`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const modulosComAulas = await Promise.all(
            (Array.isArray(modulosRes.data) ? modulosRes.data : (modulosRes.data?.modulos || modulosRes.data?.items || [])).map(async (modulo) => {
              const [aulasRes, questionariosRes] = await Promise.all([
                axios.get(`${BACKEND_URL}/api/academy/modulos/${modulo.id}/aulas`, {
                  headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${BACKEND_URL}/api/academy/modulos/${modulo.id}/questionarios`, {
                  headers: { Authorization: `Bearer ${token}` }
                })
              ]);
              return { ...modulo, aulas: aulasRes.data, questionarios: questionariosRes.data };
            })
          );
          
          return { ...curso, modulos: modulosComAulas };
        })
      );
      
      setCursos(cursosCompletos);
    } catch (error) {
      console.error("Erro ao carregar cursos:", error);
      toast.error("Erro ao carregar cursos");
    } finally {
      setLoading(false);
    }
  };

  // Upload de imagem de capa
  const handleCapaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error("Selecione uma imagem válida");
      return;
    }

    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo: 5MB");
      return;
    }

    setUploadingCapa(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/academy/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setCursoForm(prev => ({ ...prev, imagem_capa: response.data.url }));
      toast.success("Imagem enviada!");
    } catch (error) {
      console.error("Erro no upload:", error);
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploadingCapa(false);
    }
  };

  // CRUD Cursos
  const handleSaveCurso = async () => {
    if (!cursoForm.titulo.trim()) {
      toast.error("Título é obrigatório");
      return;
    }

    try {
      if (editingCurso) {
        await axios.put(`${BACKEND_URL}/api/academy/cursos/${editingCurso.id}`, cursoForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Curso atualizado!");
      } else {
        await axios.post(`${BACKEND_URL}/api/academy/cursos`, cursoForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Curso criado!");
      }
      
      setShowCursoModal(false);
      resetCursoForm();
      fetchCursos();
    } catch (error) {
      toast.error("Erro ao salvar curso");
    }
  };

  const handleDeleteCurso = async (cursoId) => {
    if (!window.confirm("Excluir este curso e todo seu conteúdo?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/academy/cursos/${cursoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Curso excluído!");
      fetchCursos();
    } catch (error) {
      toast.error("Erro ao excluir curso");
    }
  };

  // CRUD Módulos
  const handleSaveModulo = async () => {
    if (!moduloForm.titulo.trim()) {
      toast.error("Título é obrigatório");
      return;
    }

    try {
      if (editingModulo) {
        await axios.put(`${BACKEND_URL}/api/academy/modulos/${editingModulo.id}`, moduloForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Módulo atualizado!");
      } else {
        await axios.post(`${BACKEND_URL}/api/academy/modulos`, {
          ...moduloForm,
          curso_id: selectedCurso.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Módulo criado!");
      }
      
      setShowModuloModal(false);
      resetModuloForm();
      fetchCursos();
    } catch (error) {
      toast.error("Erro ao salvar módulo");
    }
  };

  const handleDeleteModulo = async (moduloId) => {
    if (!window.confirm("Excluir este módulo e suas aulas?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/academy/modulos/${moduloId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Módulo excluído!");
      fetchCursos();
    } catch (error) {
      toast.error("Erro ao excluir módulo");
    }
  };

  // CRUD Aulas
  const handleSaveAula = async () => {
    if (!aulaForm.titulo.trim()) {
      toast.error("Título é obrigatório");
      return;
    }

    try {
      if (editingAula) {
        await axios.put(`${BACKEND_URL}/api/academy/aulas/${editingAula.id}`, aulaForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Aula atualizada!");
      } else {
        await axios.post(`${BACKEND_URL}/api/academy/aulas`, {
          ...aulaForm,
          modulo_id: selectedModulo.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Aula criada!");
      }
      
      setShowAulaModal(false);
      resetAulaForm();
      fetchCursos();
    } catch (error) {
      toast.error("Erro ao salvar aula");
    }
  };

  const handleDeleteAula = async (aulaId) => {
    if (!window.confirm("Excluir esta aula?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/academy/aulas/${aulaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aula excluída!");
      fetchCursos();
    } catch (error) {
      toast.error("Erro ao excluir aula");
    }
  };

  const resetCursoForm = () => {
    setCursoForm({
      titulo: "",
      descricao: "",
      imagem_capa: "",
      perfis_permitidos: ["corretor", "cadastrador", "admin", "proprietario"],
      ativo: true
    });
    setEditingCurso(null);
  };

  const resetModuloForm = () => {
    setModuloForm({ titulo: "", descricao: "", ordem: 0 });
    setEditingModulo(null);
    setSelectedCurso(null);
  };

  const resetAulaForm = () => {
    setAulaForm({
      titulo: "",
      descricao: "",
      tipo: "video",
      conteudo_url: "",
      conteudo_texto: "",
      duracao_minutos: null
    });
    setEditingAula(null);
    setSelectedModulo(null);
  };

  const resetQuestionarioForm = () => {
    setQuestionarioForm({
      titulo: "",
      descricao: "",
      nota_minima: 70,
      tentativas_permitidas: 3,
      questoes: []
    });
    setEditingQuestionario(null);
    setSelectedModulo(null);
  };

  // CRUD Questionários
  const handleSaveQuestionario = async () => {
    if (!questionarioForm.titulo.trim()) {
      toast.error("Título é obrigatório");
      return;
    }
    if (questionarioForm.questoes.length === 0) {
      toast.error("Adicione pelo menos uma questão");
      return;
    }

    try {
      const payload = {
        ...questionarioForm,
        modulo_id: selectedModulo?.id || editingQuestionario?.modulo_id
      };

      if (editingQuestionario) {
        await axios.put(`${BACKEND_URL}/api/academy/questionarios/${editingQuestionario.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Questionário atualizado!");
      } else {
        await axios.post(`${BACKEND_URL}/api/academy/questionarios`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Questionário criado!");
      }
      
      setShowQuestionarioModal(false);
      resetQuestionarioForm();
      fetchCursos();
    } catch (error) {
      toast.error("Erro ao salvar questionário");
    }
  };

  const handleDeleteQuestionario = async (questionarioId) => {
    if (!window.confirm("Excluir este questionário?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/academy/questionarios/${questionarioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Questionário excluído!");
      fetchCursos();
    } catch (error) {
      toast.error("Erro ao excluir questionário");
    }
  };

  const addQuestao = () => {
    setQuestionarioForm(prev => ({
      ...prev,
      questoes: [...prev.questoes, {
        pergunta: "",
        opcoes: [
          { texto: "", correta: true },
          { texto: "", correta: false },
          { texto: "", correta: false },
          { texto: "", correta: false }
        ],
        explicacao: ""
      }]
    }));
  };

  const removeQuestao = (index) => {
    setQuestionarioForm(prev => ({
      ...prev,
      questoes: prev.questoes.filter((_, i) => i !== index)
    }));
  };

  const updateQuestao = (index, field, value) => {
    setQuestionarioForm(prev => ({
      ...prev,
      questoes: prev.questoes.map((q, i) => i === index ? { ...q, [field]: value } : q)
    }));
  };

  const updateOpcao = (qIndex, oIndex, field, value) => {
    setQuestionarioForm(prev => ({
      ...prev,
      questoes: prev.questoes.map((q, i) => {
        if (i !== qIndex) return q;
        return {
          ...q,
          opcoes: q.opcoes.map((op, j) => {
            if (field === 'correta' && value === true) {
              // Apenas uma opção pode ser correta
              return { ...op, correta: j === oIndex };
            }
            if (j !== oIndex) return op;
            return { ...op, [field]: value };
          })
        };
      })
    }));
  };

  const openAddQuestionario = (modulo) => {
    resetQuestionarioForm();
    setSelectedModulo(modulo);
    setShowQuestionarioModal(true);
  };

  const openEditQuestionario = async (questionario) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/academy/questionarios/${questionario.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingQuestionario(response.data);
      setQuestionarioForm({
        titulo: response.data.titulo,
        descricao: response.data.descricao || "",
        nota_minima: response.data.nota_minima,
        tentativas_permitidas: response.data.tentativas_permitidas,
        questoes: response.data.questoes || []
      });
      setShowQuestionarioModal(true);
    } catch (error) {
      toast.error("Erro ao carregar questionário");
    }
  };

  const openEditCurso = (curso) => {
    setEditingCurso(curso);
    setCursoForm({
      titulo: curso.titulo,
      descricao: curso.descricao || "",
      imagem_capa: curso.imagem_capa || "",
      perfis_permitidos: curso.perfis_permitidos || [],
      ativo: curso.ativo
    });
    setShowCursoModal(true);
  };

  const openAddModulo = (curso) => {
    setSelectedCurso(curso);
    setShowModuloModal(true);
  };

  const openEditModulo = (modulo) => {
    setEditingModulo(modulo);
    setModuloForm({
      titulo: modulo.titulo,
      descricao: modulo.descricao || "",
      ordem: modulo.ordem
    });
    setShowModuloModal(true);
  };

  const openAddAula = (modulo) => {
    setSelectedModulo(modulo);
    setShowAulaModal(true);
  };

  const openEditAula = (aula) => {
    setEditingAula(aula);
    setAulaForm({
      titulo: aula.titulo,
      descricao: aula.descricao || "",
      tipo: aula.tipo,
      conteudo_url: aula.conteudo_url || "",
      conteudo_texto: aula.conteudo_texto || "",
      duracao_minutos: aula.duracao_minutos
    });
    setShowAulaModal(true);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
      
      <div className="flex-1 p-4 lg:p-8 overflow-auto pt-20 lg:pt-8">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl lg:text-4xl font-heading font-bold text-slate-800 dark:text-slate-100">
                Gerenciar Academy
              </h1>
              <p className="text-sm lg:text-base text-slate-600 dark:text-slate-300 mt-1">
                Crie e organize cursos, módulos e aulas
              </p>
            </div>
            <button
              onClick={() => { resetCursoForm(); setShowCursoModal(true); }}
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-4 lg:px-6 py-2.5 lg:py-3 text-sm lg:text-base font-medium shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2"
              data-testid="add-curso-btn"
            >
              <Plus className="w-5 h-5" />
              Novo Curso
            </button>
          </div>

          {/* Lista de Cursos */}
          <div className="space-y-4">
            {cursos.length === 0 ? (
              <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-12 text-center">
                <GraduationCap className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400">Nenhum curso criado ainda</p>
                <p className="text-sm text-slate-500 mt-2">Clique em &quot;Novo Curso&quot; para começar</p>
              </div>
            ) : (
              cursos.map((curso) => (
                <div key={curso.id} className="glass-panel dark:bg-slate-900/80 rounded-xl overflow-hidden shadow-sm">
                  {/* Header do Curso - Clicável */}
                  <div 
                    onClick={() => setExpandedCursos(prev => ({ ...prev, [curso.id]: !prev[curso.id] }))}
                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    {/* Ícone de Expansão */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                      expandedCursos[curso.id] 
                        ? 'bg-primary/10 text-primary rotate-0' 
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-400'
                    }`}>
                      {expandedCursos[curso.id] ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </div>
                    
                    {/* Capa do Curso (miniatura) */}
                    {curso.imagem_capa ? (
                      <img 
                        src={curso.imagem_capa.startsWith('/api') ? `${BACKEND_URL}${curso.imagem_capa}` : curso.imagem_capa}
                        alt={curso.titulo}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                        <GraduationCap className="w-6 h-6 text-primary" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 dark:text-slate-100 truncate">{curso.titulo}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                          <BookOpen className="w-3 h-3" />
                          {curso.modulos?.length || 0} módulos
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                          <Play className="w-3 h-3" />
                          {curso.total_aulas || 0} aulas
                        </span>
                        {!curso.ativo && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
                            Inativo
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Ações do Curso */}
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openAddModulo(curso)}
                        className="p-2 hover:bg-primary/10 rounded-lg text-primary transition-colors"
                        title="Adicionar módulo"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditCurso(curso)}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 transition-colors"
                        title="Editar curso"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCurso(curso.id)}
                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500 transition-colors"
                        title="Excluir curso"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Conteúdo Expandido - Módulos */}
                  {expandedCursos[curso.id] && (
                    <div className="border-t border-slate-200 dark:border-slate-700">
                      {curso.modulos?.length === 0 ? (
                        <div className="p-8 text-center bg-slate-50 dark:bg-slate-800/30">
                          <BookOpen className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Nenhum módulo criado ainda
                          </p>
                          <button
                            onClick={() => openAddModulo(curso)}
                            className="mt-3 text-sm text-primary hover:underline inline-flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Adicionar primeiro módulo
                          </button>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                          {curso.modulos?.map((modulo, moduloIndex) => (
                            <div key={modulo.id} className="bg-white dark:bg-slate-800/50">
                              {/* Header do Módulo - Clicável */}
                              <div 
                                onClick={() => setExpandedModulos(prev => ({ ...prev, [modulo.id]: !prev[modulo.id] }))}
                                className="p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                              >
                                {/* Número do Módulo */}
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm transition-all ${
                                  expandedModulos[modulo.id]
                                    ? 'bg-primary text-white'
                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                }`}>
                                  {moduloIndex + 1}
                                </div>
                                
                                {/* Info do Módulo */}
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-slate-700 dark:text-slate-200">{modulo.titulo}</h4>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    <span className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                                      <Play className="w-3 h-3" />
                                      {modulo.aulas?.length || 0} aulas
                                    </span>
                                    {modulo.questionarios?.length > 0 && (
                                      <span className="text-xs text-purple-600 dark:text-purple-400 inline-flex items-center gap-1">
                                        <ClipboardList className="w-3 h-3" />
                                        {modulo.questionarios.length} questionário{modulo.questionarios.length > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Seta de Expansão */}
                                <div className={`transition-transform duration-200 ${expandedModulos[modulo.id] ? 'rotate-180' : ''}`}>
                                  <ChevronDown className="w-5 h-5 text-slate-400" />
                                </div>
                                
                                {/* Ações do Módulo */}
                                <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => openAddAula(modulo)}
                                    className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-blue-500 transition-colors"
                                    title="Adicionar aula"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => openEditModulo(modulo)}
                                    className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-500 transition-colors"
                                    title="Editar módulo"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteModulo(modulo.id)}
                                    className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500 transition-colors"
                                    title="Excluir módulo"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              
                              {/* Conteúdo Expandido - Aulas e Questionários */}
                              {expandedModulos[modulo.id] && (
                                <div className="px-4 pb-4 pt-0 ml-11">
                                  {/* Seção de Aulas */}
                                  <div className="mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Video className="w-4 h-4 text-blue-500" />
                                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Aulas</span>
                                      <button
                                        onClick={() => openAddAula(modulo)}
                                        className="ml-auto p-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded text-blue-500"
                                        title="Adicionar aula"
                                      >
                                        <Plus className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    
                                    {modulo.aulas?.length === 0 ? (
                                      <div className="py-3 px-4 bg-slate-50 dark:bg-slate-700/30 rounded-lg text-center">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Nenhuma aula adicionada</p>
                                      </div>
                                    ) : (
                                      <div className="space-y-1 bg-slate-50 dark:bg-slate-700/30 rounded-lg p-2">
                                        {modulo.aulas?.map((aula, aulaIndex) => (
                                          <div 
                                            key={aula.id} 
                                            className="flex items-center gap-3 p-2 bg-white dark:bg-slate-800 rounded-lg hover:shadow-sm transition-shadow"
                                          >
                                            <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-medium">
                                              {aulaIndex + 1}
                                            </span>
                                            {aula.tipo === 'video' ? (
                                              <Video className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                            ) : aula.tipo === 'pdf' ? (
                                              <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                            ) : (
                                              <FileText className="w-4 h-4 text-green-500 flex-shrink-0" />
                                            )}
                                            <span className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate">{aula.titulo}</span>
                                            {aula.duracao_minutos && (
                                              <span className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                                                {aula.duracao_minutos} min
                                              </span>
                                            )}
                                            <button
                                              onClick={() => openEditAula(aula)}
                                              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded opacity-60 hover:opacity-100 transition-opacity"
                                            >
                                              <Pencil className="w-3.5 h-3.5 text-slate-500" />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteAula(aula.id)}
                                              className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-60 hover:opacity-100 transition-opacity"
                                            >
                                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Seção de Questionários */}
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <ClipboardList className="w-4 h-4 text-purple-500" />
                                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Questionários</span>
                                      <button
                                        onClick={() => openAddQuestionario(modulo)}
                                        className="ml-auto p-1 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded text-purple-500"
                                        title="Adicionar questionário"
                                        data-testid={`add-questionario-btn-${modulo.id}`}
                                      >
                                        <Plus className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    
                                    {modulo.questionarios?.length === 0 ? (
                                      <div className="py-3 px-4 bg-purple-50 dark:bg-purple-900/10 rounded-lg text-center border border-dashed border-purple-200 dark:border-purple-800">
                                        <p className="text-xs text-purple-600 dark:text-purple-400">
                                          Adicione um questionário para avaliar o aprendizado
                                        </p>
                                      </div>
                                    ) : (
                                      <div className="space-y-1 bg-purple-50 dark:bg-purple-900/10 rounded-lg p-2">
                                        {modulo.questionarios?.map((questionario) => (
                                          <div 
                                            key={questionario.id} 
                                            className="flex items-center gap-3 p-2 bg-white dark:bg-slate-800 rounded-lg hover:shadow-sm transition-shadow"
                                          >
                                            <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                                              <ClipboardList className="w-3 h-3" />
                                            </div>
                                            <span className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate">{questionario.titulo}</span>
                                            <span className="text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 px-2 py-0.5 rounded-full">
                                              {questionario.total_questoes || questionario.questoes?.length || 0} questões
                                            </span>
                                            <button
                                              onClick={() => openEditQuestionario(questionario)}
                                              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded opacity-60 hover:opacity-100 transition-opacity"
                                              data-testid={`edit-questionario-btn-${questionario.id}`}
                                            >
                                              <Pencil className="w-3.5 h-3.5 text-slate-500" />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteQuestionario(questionario.id)}
                                              className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-60 hover:opacity-100 transition-opacity"
                                              data-testid={`delete-questionario-btn-${questionario.id}`}
                                            >
                                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal Curso */}
      {showCursoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                {editingCurso ? "Editar Curso" : "Novo Curso"}
              </h3>
              <button onClick={() => { setShowCursoModal(false); resetCursoForm(); }}>
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Título *</label>
                <input
                  type="text"
                  value={cursoForm.titulo}
                  onChange={(e) => setCursoForm({ ...cursoForm, titulo: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                  placeholder="Ex: Técnicas de Vendas"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Descrição</label>
                <textarea
                  value={cursoForm.descricao}
                  onChange={(e) => setCursoForm({ ...cursoForm, descricao: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100 resize-none"
                  rows={3}
                  placeholder="Descrição do curso..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Imagem de Capa</label>
                <input
                  type="file"
                  ref={capaInputRef}
                  onChange={handleCapaUpload}
                  accept="image/*"
                  className="hidden"
                />
                {cursoForm.imagem_capa ? (
                  <div className="relative">
                    <img 
                      src={cursoForm.imagem_capa.startsWith('/api') ? `${BACKEND_URL}${cursoForm.imagem_capa}` : cursoForm.imagem_capa}
                      alt="Capa do curso"
                      className="w-full h-32 object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                    />
                    <button
                      type="button"
                      onClick={() => setCursoForm({ ...cursoForm, imagem_capa: "" })}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => capaInputRef.current?.click()}
                    disabled={uploadingCapa}
                    className="w-full h-32 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    {uploadingCapa ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-slate-400" />
                        <span className="text-sm text-slate-500">Clique para enviar imagem</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cursoForm.ativo}
                    onChange={(e) => setCursoForm({ ...cursoForm, ativo: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Curso ativo</span>
                </label>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
              <button
                onClick={() => { setShowCursoModal(false); resetCursoForm(); }}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveCurso}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Módulo */}
      {showModuloModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-lg">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                {editingModulo ? "Editar Módulo" : "Novo Módulo"}
              </h3>
              <button onClick={() => { setShowModuloModal(false); resetModuloForm(); }}>
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Título *</label>
                <input
                  type="text"
                  value={moduloForm.titulo}
                  onChange={(e) => setModuloForm({ ...moduloForm, titulo: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                  placeholder="Ex: Módulo 1 - Introdução"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Descrição</label>
                <textarea
                  value={moduloForm.descricao}
                  onChange={(e) => setModuloForm({ ...moduloForm, descricao: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100 resize-none"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Ordem</label>
                <input
                  type="number"
                  value={moduloForm.ordem}
                  onChange={(e) => setModuloForm({ ...moduloForm, ordem: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                />
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
              <button
                onClick={() => { setShowModuloModal(false); resetModuloForm(); }}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveModulo}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aula */}
      {showAulaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                {editingAula ? "Editar Aula" : "Nova Aula"}
              </h3>
              <button onClick={() => { setShowAulaModal(false); resetAulaForm(); }}>
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Título *</label>
                <input
                  type="text"
                  value={aulaForm.titulo}
                  onChange={(e) => setAulaForm({ ...aulaForm, titulo: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                  placeholder="Ex: Aula 1 - Boas vindas"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tipo</label>
                <select
                  value={aulaForm.tipo}
                  onChange={(e) => setAulaForm({ ...aulaForm, tipo: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                >
                  <option value="video">Vídeo</option>
                  <option value="pdf">PDF</option>
                  <option value="texto">Texto</option>
                </select>
              </div>
              {(aulaForm.tipo === 'video' || aulaForm.tipo === 'pdf') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {aulaForm.tipo === 'video' ? 'URL do Vídeo (YouTube, Vimeo ou direto)' : 'URL do PDF'}
                  </label>
                  <input
                    type="text"
                    value={aulaForm.conteudo_url}
                    onChange={(e) => setAulaForm({ ...aulaForm, conteudo_url: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                    placeholder="https://..."
                  />
                </div>
              )}
              {aulaForm.tipo === 'texto' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Conteúdo</label>
                  <textarea
                    value={aulaForm.conteudo_texto}
                    onChange={(e) => setAulaForm({ ...aulaForm, conteudo_texto: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100 resize-none"
                    rows={6}
                    placeholder="Conteúdo da aula..."
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Descrição</label>
                <textarea
                  value={aulaForm.descricao}
                  onChange={(e) => setAulaForm({ ...aulaForm, descricao: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100 resize-none"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Duração (minutos)</label>
                <input
                  type="number"
                  value={aulaForm.duracao_minutos || ""}
                  onChange={(e) => setAulaForm({ ...aulaForm, duracao_minutos: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                  placeholder="Ex: 15"
                />
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
              <button
                onClick={() => { setShowAulaModal(false); resetAulaForm(); }}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveAula}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Questionário */}
      {showQuestionarioModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center sticky top-0 bg-white dark:bg-slate-900 z-10">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                {editingQuestionario ? "Editar Questionário" : "Novo Questionário"}
              </h3>
              <button onClick={() => { setShowQuestionarioModal(false); resetQuestionarioForm(); }} data-testid="close-questionario-modal">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Informações básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Título *</label>
                  <input
                    type="text"
                    value={questionarioForm.titulo}
                    onChange={(e) => setQuestionarioForm({ ...questionarioForm, titulo: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                    placeholder="Ex: Avaliação do Módulo 1"
                    data-testid="questionario-titulo-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Descrição</label>
                  <textarea
                    value={questionarioForm.descricao}
                    onChange={(e) => setQuestionarioForm({ ...questionarioForm, descricao: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100 resize-none"
                    rows={2}
                    placeholder="Instruções para o questionário..."
                    data-testid="questionario-descricao-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nota Mínima (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={questionarioForm.nota_minima}
                    onChange={(e) => setQuestionarioForm({ ...questionarioForm, nota_minima: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                    data-testid="questionario-nota-minima-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tentativas Permitidas</label>
                  <input
                    type="number"
                    min="0"
                    value={questionarioForm.tentativas_permitidas}
                    onChange={(e) => setQuestionarioForm({ ...questionarioForm, tentativas_permitidas: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 rounded-lg glass-input dark:text-slate-100"
                    placeholder="0 = ilimitado"
                    data-testid="questionario-tentativas-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">0 = tentativas ilimitadas</p>
                </div>
              </div>

              {/* Questões */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-800 dark:text-slate-100 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-purple-500" />
                    Questões ({questionarioForm.questoes.length})
                  </h4>
                  <button
                    type="button"
                    onClick={addQuestao}
                    className="px-3 py-1.5 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 flex items-center gap-1"
                    data-testid="add-questao-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar Questão
                  </button>
                </div>

                {questionarioForm.questoes.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 dark:bg-slate-800/50 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700">
                    <ClipboardList className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                    <p className="text-slate-500 dark:text-slate-400">Nenhuma questão adicionada</p>
                    <p className="text-sm text-slate-400 dark:text-slate-500">Clique em &quot;Adicionar Questão&quot; para começar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {questionarioForm.questoes.map((questao, qIndex) => (
                      <div 
                        key={qIndex} 
                        className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <span className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 flex items-center justify-center font-bold text-sm flex-shrink-0">
                            {qIndex + 1}
                          </span>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={questao.pergunta}
                              onChange={(e) => updateQuestao(qIndex, 'pergunta', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg glass-input dark:text-slate-100 text-sm"
                              placeholder="Digite a pergunta..."
                              data-testid={`questao-${qIndex}-pergunta`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeQuestao(qIndex)}
                            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-red-500"
                            title="Remover questão"
                            data-testid={`remove-questao-${qIndex}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Opções */}
                        <div className="ml-10 space-y-2">
                          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                            Opções (marque a correta):
                          </p>
                          {questao.opcoes.map((opcao, oIndex) => (
                            <div key={oIndex} className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => updateOpcao(qIndex, oIndex, 'correta', true)}
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                  opcao.correta 
                                    ? 'border-green-500 bg-green-500 text-white' 
                                    : 'border-slate-300 dark:border-slate-600 hover:border-green-400'
                                }`}
                                title={opcao.correta ? "Resposta correta" : "Marcar como correta"}
                                data-testid={`opcao-${qIndex}-${oIndex}-correta`}
                              >
                                {opcao.correta && <CheckCircle className="w-3 h-3" />}
                              </button>
                              <input
                                type="text"
                                value={opcao.texto}
                                onChange={(e) => updateOpcao(qIndex, oIndex, 'texto', e.target.value)}
                                className="flex-1 px-3 py-1.5 rounded glass-input dark:text-slate-100 text-sm"
                                placeholder={`Opção ${oIndex + 1}`}
                                data-testid={`opcao-${qIndex}-${oIndex}-texto`}
                              />
                            </div>
                          ))}
                        </div>

                        {/* Explicação */}
                        <div className="ml-10 mt-3">
                          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                            Explicação (opcional)
                          </label>
                          <textarea
                            value={questao.explicacao || ""}
                            onChange={(e) => updateQuestao(qIndex, 'explicacao', e.target.value)}
                            className="w-full px-3 py-2 rounded glass-input dark:text-slate-100 text-sm resize-none"
                            rows={2}
                            placeholder="Explicação da resposta correta..."
                            data-testid={`questao-${qIndex}-explicacao`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-slate-900">
              <button
                onClick={() => { setShowQuestionarioModal(false); resetQuestionarioForm(); }}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveQuestionario}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2"
                data-testid="save-questionario-btn"
              >
                <Save className="w-4 h-4" />
                Salvar Questionário
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyAdmin;
